import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";

const CircleTargets = () => {
  const [circles, setCircles] = useState([]);
  const [targetValues, setTargetValues] = useState({});
  const [newCircle, setNewCircle] = useState("");
  const [newTarget, setNewTarget] = useState("25");
  const [loading, setLoading] = useState(true);
  const [savingCircle, setSavingCircle] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadTargets = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/users/circle-targets");
      setCircles(data);
      setTargetValues(
        Object.fromEntries(
          data.map(({ circle, target }) => [circle, String(target)])
        )
      );
    } catch (err) {
      console.error("Failed to load circle targets:", err);
      setError(err.response?.data?.error || "Failed to load circle targets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTargets();
  }, []);

  const saveTarget = async (circle, target) => {
    const parsedTarget = Number(target);
    if (!Number.isSafeInteger(parsedTarget) || parsedTarget < 1) {
      setError("Target must be a positive whole number.");
      setNotice("");
      return false;
    }

    setSavingCircle(circle);
    setError("");
    setNotice("");
    try {
      await axios.put("/users/circle-targets", { circle, target: parsedTarget });
      await loadTargets();
      setNotice(`Target for ${circle} saved.`);
      return true;
    } catch (err) {
      console.error("Failed to save circle target:", err);
      setError(err.response?.data?.error || `Failed to save target for ${circle}.`);
      return false;
    } finally {
      setSavingCircle("");
    }
  };

  const addCircleTarget = async (event) => {
    event.preventDefault();
    const circle = newCircle.trim();
    if (!circle) {
      setError("Enter a circle name.");
      return;
    }
    const saved = await saveTarget(circle, newTarget);
    if (saved) {
      setNewCircle("");
      setNewTarget("25");
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="mx-auto max-w-5xl">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-teal-700">Circle Targets</h1>
            <p className="mt-2 text-sm text-gray-600">
              Set the property target used by S1D inspectors. Each inspector
              receives the target configured for their assigned circle.
            </p>
          </header>

          {error && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {notice && (
            <div role="status" className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">
              {notice}
            </div>
          )}

          <form
            onSubmit={addCircleTarget}
            className="mb-6 grid gap-3 rounded-xl bg-white p-5 shadow-sm sm:grid-cols-[1fr_180px_auto] sm:items-end"
          >
            <label className="text-sm font-medium text-gray-700">
              Circle name
              <input
                value={newCircle}
                onChange={(event) => setNewCircle(event.target.value)}
                maxLength={255}
                required
                className="mt-1 block w-full rounded-lg border border-gray-300 p-2"
                placeholder="Enter a circle"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Target
              <input
                type="number"
                min="1"
                step="1"
                required
                value={newTarget}
                onChange={(event) => setNewTarget(event.target.value)}
                className="mt-1 block w-full rounded-lg border border-gray-300 p-2"
              />
            </label>
            <button
              type="submit"
              disabled={savingCircle !== ""}
              className="rounded-lg bg-teal-600 px-5 py-2 font-medium text-white hover:bg-teal-700 disabled:opacity-60"
            >
              Add target
            </button>
          </form>

          <section className="overflow-hidden rounded-xl bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="font-semibold text-gray-800">Targets by circle</h2>
            </div>
            {loading ? (
              <p className="p-5 text-sm text-gray-500">Loading circle targets...</p>
            ) : circles.length === 0 ? (
              <p className="p-5 text-sm text-gray-500">
                No circles found yet. Add a target above or assign a circle to a user.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-left text-gray-600">
                    <tr>
                      <th className="px-5 py-3 font-medium">Circle</th>
                      <th className="px-5 py-3 font-medium">Target</th>
                      <th className="px-5 py-3 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {circles.map(({ circle }) => (
                      <tr key={circle} className="border-t border-gray-100">
                        <td className="px-5 py-3 font-medium text-gray-800">{circle}</td>
                        <td className="px-5 py-3">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            required
                            aria-label={`Target for ${circle}`}
                            value={targetValues[circle] ?? ""}
                            onChange={(event) =>
                              setTargetValues((current) => ({
                                ...current,
                                [circle]: event.target.value,
                              }))
                            }
                            className="w-32 rounded-lg border border-gray-300 p-2"
                          />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => saveTarget(circle, targetValues[circle])}
                            disabled={savingCircle !== ""}
                            className="rounded-lg bg-teal-600 px-4 py-2 font-medium text-white hover:bg-teal-700 disabled:opacity-60"
                          >
                            {savingCircle === circle ? "Saving..." : "Save"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
              Circles without a saved target continue to use the existing default of 25.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CircleTargets;
